import { useEffect, useRef } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { Link } from '@tanstack/react-router';
import { Lock } from 'lucide-react';
import { padroesDoTipo, tiposDeItemOferecidos, type AtividadeEmpresa, type ItemFormValores, type UnidadeResponse } from '@triade/contracts';
import { useFatorUnidade, useUnidades } from '@/api/unidades';
import { SeletorCategoria, SEM_CATEGORIA } from '@/components/categorias/seletor-categoria';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatarQuantidade } from '@/lib/format';
import { campoDoProtheus, TITULO_CAMPO_PROTHEUS } from '@/components/integracoes-erp/selo-protheus';
import { CLASSIFICACOES, ROTULO_CLASSIFICACAO, ROTULO_TIPO, TIPOS, type TipoItem } from './tipos';

const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const TRIGGER = 'h-10 w-full rounded-[10px] bg-card';
/** Valor do select "igual a unidade de venda" (Radix nao aceita valor vazio). Gravado como undefined. */
const IGUAL_VENDA = '__igual_venda__';

/** Opcoes do seletor de unidade: as cadastradas ("KG — Quilograma"). Valor gravado fora do cadastro (sem diferenciar caixa) entra como "X (não cadastrada)". */
function opcoesDeUnidade(atual: string | undefined, cadastradas: Pick<UnidadeResponse, 'codigo' | 'descricao'>[]) {
  const achada = atual ? cadastradas.find((u) => u.codigo.toLowerCase() === atual.toLowerCase()) : undefined;
  const opcoes = cadastradas.map((u) => ({ valor: u.codigo, rotulo: `${u.codigo} — ${u.descricao}` }));
  if (atual && !achada) opcoes.unshift({ valor: atual, rotulo: `${atual} (não cadastrada)` });
  return { selecionado: achada?.codigo ?? atual, opcoes };
}

/** Compara fatores decimais pelo texto, sem zeros de escala ("1000.000000" = "1000"). Nao calcula nada. */
const mesmoFator = (a: string | undefined, b: string | undefined) => (a ?? '').replace(/\.?0+$/, '') === (b ?? '').replace(/\.?0+$/, '');

export function MensagemCampo({ id, mensagem }: { id: string; mensagem: string | undefined }) {
  if (!mensagem) return null;
  return (
    <p id={id} role="alert" className="mt-1 text-caption text-danger-ink">
      {mensagem}
    </p>
  );
}

export interface DadosItemProps {
  form: UseFormReturn<ItemFormValores, unknown, ItemFormValores>;
  /** Item ainda nao gravado: codigo e tipo editaveis. Gravado: os dois sao fixos (a API nao os altera). */
  novo: boolean;
  /** Perfil de atividade da empresa (so exibicao): restringe os tipos oferecidos. Sem ele, todos. */
  atividades?: readonly AtividadeEmpresa[];
  /** Item vindo do Protheus: descricao, unidade e ativo so se editam la (campos travados em `CAMPOS_DO_PROTHEUS.Item`). */
  doProtheus?: boolean;
}

/** Bloco "Informacoes gerais" (design Item.dc.html): codigo, nome, unidade, tipo e ativo. So entrada; a validacao e o schema dos contratos. */
export function DadosItem({ form, novo, atividades, doProtheus }: DadosItemProps) {
  const { register, control, formState } = form;
  const travado = (campo: string) => campoDoProtheus('Item', doProtheus, campo);
  const oferecidos = tiposDeItemOferecidos(atividades);
  const tipoAtual = form.watch('tipo');
  // Item novo: o tipo padrao sai do perfil (empresa so COMERCIO nao comeca em "Produto fabricado"). Sem sujar o formulario.
  useEffect(() => {
    const lista = tiposDeItemOferecidos(atividades);
    if (!novo || lista.length === 0 || lista.includes(tipoAtual)) return;
    const primeiro = lista[0] as TipoItem;
    const padrao = padroesDoTipo(primeiro);
    form.setValue('tipo', primeiro, { shouldDirty: false });
    form.setValue('compoeFichaTecnica', padrao.compoeFichaTecnica, { shouldDirty: false });
    form.setValue('comerciavel', padrao.comerciavel, { shouldDirty: false });
  }, [novo, atividades, tipoAtual, form]);
  const erros = formState.errors;

  // Unidades: o seletor vem do cadastro. Fator sugerido so quando as duas unidades estao escolhidas e sao diferentes (de = custo, para = venda).
  const unidades = useUnidades().data?.unidades ?? [];
  const unidade = form.watch('unidade') ?? '';
  const unidadeCusto = form.watch('unidadeCusto') ?? '';
  const converte = unidadeCusto.length > 0 && unidade.length > 0 && unidadeCusto.toLowerCase() !== unidade.toLowerCase();
  const conversao = useFatorUnidade(converte ? unidadeCusto : '', converte ? unidade : '');
  const fatorSugerido = converte ? (conversao.data?.fator ?? null) : null;
  const semConversao = converte && conversao.isSuccess && conversao.data.fator === null;
  const fatorAtual = form.watch('fatorCustoVenda');
  // Preenchido: "1 t = 1000 kg" ao lado do campo (so texto; o fator e o que o usuario digitou ou o sugerido).
  const fatorTexto = typeof fatorAtual === 'string' ? fatorAtual.trim() : '';
  const equivalencia = converte && fatorTexto !== '' && formatarQuantidade(fatorTexto) !== '—' ? `1 ${unidadeCusto} = ${formatarQuantidade(fatorTexto)} ${unidade}` : null;
  // Sugestao do fator: so quando o PAR de unidades muda (escolha do usuario), e so se o usuario ainda nao digitou no fator.
  // O item ja gravado nao muda sozinho ao abrir: a primeira leitura do par apenas registra.
  const par = converte ? `${unidadeCusto}|${unidade}` : '';
  const parAnterior = useRef<string | null>(null);
  const aguardaSugestao = useRef(false);
  useEffect(() => {
    if (parAnterior.current !== null && parAnterior.current !== par) aguardaSugestao.current = true;
    parAnterior.current = par;
    if (!aguardaSugestao.current || fatorSugerido === null) return;
    aguardaSugestao.current = false;
    if (form.getFieldState('fatorCustoVenda').isDirty || mesmoFator(form.getValues('fatorCustoVenda'), fatorSugerido)) return;
    form.setValue('fatorCustoVenda', fatorSugerido, { shouldDirty: true });
  }, [par, fatorSugerido, form]);

  return (
    <section aria-label="Informações gerais" className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4 rounded-2xl border border-border bg-card p-5">
      <div>
        <Label htmlFor="i-cod" className={ROTULO}>
          Código
        </Label>
        <Input
          id="i-cod"
          className="font-mono"
          maxLength={40}
          readOnly={!novo}
          aria-readonly={!novo || undefined}
          aria-invalid={erros.codigo ? true : undefined}
          aria-describedby={erros.codigo ? 'i-cod-erro' : undefined}
          {...register('codigo')}
        />
        <MensagemCampo id="i-cod-erro" mensagem={erros.codigo?.message} />
        {!novo && <p className="mt-1 text-caption text-ink-muted">O código não muda depois de salvo.</p>}
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor="i-nome" className={ROTULO}>
          Nome
        </Label>
        <Input
          id="i-nome"
          maxLength={200}
          disabled={travado('descricao')}
          title={travado('descricao') ? TITULO_CAMPO_PROTHEUS : undefined}
          aria-invalid={erros.descricao ? true : undefined}
          aria-describedby={erros.descricao ? 'i-nome-erro' : undefined}
          {...register('descricao')}
        />
        <MensagemCampo id="i-nome-erro" mensagem={erros.descricao?.message} />
      </div>
      <div>
        <Label htmlFor="i-un" className={ROTULO}>
          Unidade
        </Label>
        <Controller
          control={control}
          name="unidade"
          render={({ field }) => {
            const { selecionado, opcoes } = opcoesDeUnidade(field.value, unidades);
            return (
              <Select value={selecionado} onValueChange={field.onChange} disabled={travado('unidade')}>
                <SelectTrigger id="i-un" className={TRIGGER} aria-invalid={erros.unidade ? true : undefined} title={travado('unidade') ? TITULO_CAMPO_PROTHEUS : undefined}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {opcoes.map((o) => (
                    <SelectItem key={o.valor} value={o.valor}>
                      {o.rotulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          }}
        />
        <MensagemCampo id="i-un-erro" mensagem={erros.unidade?.message} />
      </div>
      <div>
        <Label htmlFor="i-un-custo" className={ROTULO}>
          Unidade de custo (opcional)
        </Label>
        <Controller
          control={control}
          name="unidadeCusto"
          render={({ field }) => {
            const { selecionado, opcoes } = opcoesDeUnidade(field.value, unidades);
            return (
              <Select value={selecionado ?? IGUAL_VENDA} onValueChange={(v) => field.onChange(v === IGUAL_VENDA ? undefined : v)}>
                <SelectTrigger id="i-un-custo" className={TRIGGER} aria-invalid={erros.unidadeCusto ? true : undefined} aria-describedby={erros.unidadeCusto ? 'i-un-custo-erro' : 'i-un-custo-ajuda'}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={IGUAL_VENDA}>— igual à unidade de venda —</SelectItem>
                  {opcoes.map((o) => (
                    <SelectItem key={o.valor} value={o.valor}>
                      {o.rotulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          }}
        />
        <MensagemCampo id="i-un-custo-erro" mensagem={erros.unidadeCusto?.message} />
        <p id="i-un-custo-ajuda" className="mt-1 text-caption text-ink-muted">
          Unidade em que a ficha ou a compra custa, se for diferente da unidade (ex.: custo em t, venda em kg).
        </p>
      </div>
      {converte && (
        <div>
          <Label htmlFor="i-fator" className={ROTULO}>
            Fator de custo para venda (opcional)
          </Label>
          <div className="flex items-center gap-3">
            <Input
              id="i-fator"
              inputMode="decimal"
              placeholder="Ex.: 1000"
              aria-invalid={erros.fatorCustoVenda ? true : undefined}
              aria-describedby={erros.fatorCustoVenda ? 'i-fator-erro' : 'i-fator-ajuda'}
              {...register('fatorCustoVenda', { setValueAs: (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v) })}
            />
            {equivalencia && (
              <span data-testid="fator-equivalencia" className="whitespace-nowrap text-small text-ink-secondary">
                {equivalencia}
              </span>
            )}
          </div>
          <MensagemCampo id="i-fator-erro" mensagem={erros.fatorCustoVenda?.message} />
          <p id="i-fator-ajuda" className="mt-1 text-caption text-ink-muted">
            Quantas unidades de venda cabem em 1 de custo: t para kg = 1000. Use ponto decimal. Vazio = 1.
          </p>
          {fatorSugerido !== null && mesmoFator(fatorAtual, fatorSugerido) && (
            <p id="i-fator-sugerido" role="status" className="mt-1 text-caption text-ink-muted">
              Sugerido pelo cadastro de unidades (1 {unidadeCusto} = {formatarQuantidade(fatorSugerido)} {unidade}); você pode editar.
            </p>
          )}
          {semConversao && (
            <p id="i-fator-sem-conversao" role="status" className="mt-1 text-caption text-ink-body">
              Sem conversão cadastrada entre {unidadeCusto} e {unidade} — informe o fator ou{' '}
              <Link to="/cadastros/unidades" className="font-medium text-primary hover:underline">
                cadastre em Cadastros › Unidades
              </Link>
              .
            </p>
          )}
        </div>
      )}
      <div>
        <Label htmlFor="i-tipo" className={ROTULO}>
          Tipo
        </Label>
        <Controller
          control={control}
          name="tipo"
          render={({ field }) => (
            <Select
              value={field.value}
              disabled={!novo}
              onValueChange={(t) => {
                // Padrao das flags por tipo (os do XARP): ao escolher o tipo, as duas flags assumem o padrao; editaveis depois.
                const padrao = padroesDoTipo(t as TipoItem);
                field.onChange(t);
                form.setValue('compoeFichaTecnica', padrao.compoeFichaTecnica, { shouldDirty: true });
                form.setValue('comerciavel', padrao.comerciavel, { shouldDirty: true });
              }}
            >
              <SelectTrigger id="i-tipo" className={TRIGGER} aria-describedby="i-tipo-ajuda">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {/* Tipo atual fora do perfil continua listado so enquanto e o atual (item gravado; no novo, o efeito acima troca pelo do perfil). */}
                {TIPOS.filter((t) => oferecidos.includes(t) || t === field.value).map((t) => (
                  <SelectItem key={t} value={t}>
                    {ROTULO_TIPO[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        <p id="i-tipo-ajuda" className="mt-1 text-caption text-ink-muted">
          {novo ? (
            'Define de onde vem o custo: ficha técnica (produto e serviço) ou aquisição (os demais). Não muda depois de salvo.'
          ) : (
            <>
              <Lock aria-hidden="true" className="mr-1 inline size-3 align-[-1px]" />
              Tipo travado: define de onde vem o custo e não muda depois de salvo. Para outro tipo, cadastre um novo item.
            </>
          )}
        </p>
      </div>
      <div>
        <Controller
          control={control}
          name="categoriaId"
          render={({ field }) => (
            <SeletorCategoria
              id="i-categoria"
              rotulo="Categoria"
              value={field.value}
              // "Sem categoria" grava como vazio: null no PATCH e omitido no POST (o pai decide).
              onChange={(v) => field.onChange(v === SEM_CATEGORIA ? undefined : v)}
              className={TRIGGER}
            />
          )}
        />
      </div>
      {form.watch('tipo') === 'PRODUTO' && (
        <div>
          <Label htmlFor="i-classificacao" className={ROTULO}>
            Classificação
          </Label>
          <Controller
            control={control}
            name="classificacao"
            render={({ field }) => (
              <select
                id="i-classificacao"
                className={`${TRIGGER} rounded-[10px] border border-input px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none`}
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? undefined : e.target.value)}
                aria-describedby="i-classificacao-ajuda"
              >
                <option value="">Não classificado</option>
                {CLASSIFICACOES.map((c) => (
                  <option key={c} value={c}>
                    {ROTULO_CLASSIFICACAO[c]}
                  </option>
                ))}
              </select>
            )}
          />
          <p id="i-classificacao-ajuda" className="mt-1 text-caption text-ink-muted">
            Só produto fabricado: intermediário, conjunto ou acabado.
          </p>
        </div>
      )}
      <div className="col-span-full flex flex-wrap gap-6 border-t border-dashed border-border pt-3.5">
        <label htmlFor="i-ativo" className="flex cursor-pointer items-center gap-2">
          <input id="i-ativo" type="checkbox" disabled={novo || travado('ativo')} title={travado('ativo') ? TITULO_CAMPO_PROTHEUS : undefined} className="size-[18px] accent-primary" {...register('ativo')} />
          Ativo
        </label>
        {novo && <span className="text-small text-ink-muted">Todo item novo entra ativo.</span>}
      </div>
      <fieldset className="col-span-full grid gap-4 border-t border-dashed border-border pt-3.5 sm:grid-cols-2">
        <legend className="sr-only">Flags do item</legend>
        <label htmlFor="i-compoe" className="flex cursor-pointer items-start gap-2">
          <input id="i-compoe" type="checkbox" role="switch" aria-describedby="i-compoe-ajuda" className="mt-0.5 size-[18px] accent-primary" {...register('compoeFichaTecnica')} />
          <span>
            Compõe ficha técnica
            <span id="i-compoe-ajuda" className="block text-caption text-ink-muted">
              Pode ser usado como componente da ficha de outro item. Desligar é recusado se o item já está numa ficha ativa (a API diz quais).
            </span>
          </span>
        </label>
        <label htmlFor="i-comerciavel" className="flex cursor-pointer items-start gap-2">
          <input id="i-comerciavel" type="checkbox" role="switch" aria-describedby="i-comerciavel-ajuda" className="mt-0.5 size-[18px] accent-primary" {...register('comerciavel')} />
          <span>
            Comerciável
            <span id="i-comerciavel-ajuda" className="block text-caption text-ink-muted">
              Entra na formação de preço, na lista de preços e no orçamento. Sem ela, o item não é precificado.
            </span>
          </span>
        </label>
      </fieldset>
    </section>
  );
}
